import logging from 'utils/logging';
import { MOMENT_TYPES, NARRATION_KEYS, HOST_STATUS } from '../hostStateSlice';
import {
  GAMESTATE_LOBBY,
  GAMESTATE_ROUND_FINAL,
} from '../../gamestate/constants';
import { advanceState } from '../../gamestate/GameState';
import { determineNextRound } from '../../gamestate/roundCompletionUtils';
import { PLAYER_STATES, getInterRoundPhase } from '../../player/playerConstants';
import { registerCacheReset } from '../momentCacheManager';

const log = logging.getLogger('moments.FinalIntro');
log.setLevel('debug');

let triggered = false;

export const resetFinalIntroCache = () => {
  triggered = false;
};
registerCacheReset('round', resetFinalIntroCache);

/**
 * Trigger: fires as soon as the inter-round lobby opens and the next round is final.
 * This generates the narration early so it's ready by the time all players click Ready.
 */
export const FinalIntroTrigger = {
  id: 'FINAL_INTRO',
  shouldTrigger: ({ currentState, currentPlayers, gameObject }) => {
    if (triggered) return false;

    // Fire during inter-round lobby (round is set = inter-round, not first lobby)
    if (currentState?.currentState !== GAMESTATE_LOBBY) return false;
    if (!currentState?.round) return false;

    // Check that the next round is final
    const nextRound = determineNextRound(currentState.round, gameObject?.categories);
    if (nextRound !== GAMESTATE_ROUND_FINAL) return false;

    if (!currentPlayers || currentPlayers.length === 0) return false;

    triggered = true;
    log.info('Final intro moment triggered (early generation during inter-round lobby)');

    const playerScores = currentPlayers.map(p => ({
      name: p.name,
      score: p.score || 0,
    }));

    return {
      shouldTriggerAction: false,
      shouldGenerateNarration: true,
      narrationParams: {
        narrationKey: NARRATION_KEYS.FINAL_INTRO,
        momentType: MOMENT_TYPES.FINAL_INTRO,
        context: {
          playerNames: currentPlayers.map(p => p.name),
          playerScores,
        },
      },
    };
  },
};

/**
 * Playback: waits for all players to be ready in the inter-round lobby,
 * then plays the narration. onComplete advances to ROUND/final/waiting.
 */
export const FinalIntroPlayback = {
  id: 'FINAL_INTRO_PLAYBACK',
  momentType: MOMENT_TYPES.FINAL_INTRO,

  shouldPlay: (gameState, hostState, narration, playedNarrations, players) => {
    if (!narration || narration.momentType !== MOMENT_TYPES.FINAL_INTRO) return false;
    if (playedNarrations.has(narration.key)) {
      log.debug('FinalIntro shouldPlay: already played');
      return false;
    }

    // Must be in the inter-round lobby
    if (gameState?.currentState !== GAMESTATE_LOBBY) return false;
    if (!gameState?.round) return false;

    // Narration must be ready
    const ready = narration.textStatus === 'READY' && (narration.audioStatus === 'READY' || narration.audioStatus === 'ERROR');
    if (!ready) {
      log.debug(`FinalIntro shouldPlay: narration not ready (text=${narration.textStatus}, audio=${narration.audioStatus})`);
      return false;
    }

    if (hostState?.status === HOST_STATUS.SPEAKING) {
      log.debug('FinalIntro shouldPlay: host speaking');
      return false;
    }

    // All players must be ready
    if (!players || players.length === 0) {
      log.debug(`FinalIntro shouldPlay: no players (count=${players?.length})`);
      return false;
    }
    const phase = getInterRoundPhase(gameState.round);
    const playerReadiness = players.map(p => ({
      name: p.name,
      readyState: p.playerState?.[phase]?.state,
    }));
    const allReady = players.every(p =>
      p.playerState?.[phase]?.state === PLAYER_STATES.INTER_ROUND.READY
    );
    log.debug(`FinalIntro shouldPlay: phase=${phase}, allReady=${allReady}`, playerReadiness);
    if (!allReady) return false;

    log.info('All players ready and narration ready — playing Final Intro');
    return true;
  },

  onComplete: async (gameId, dispatch, getState) => {
    log.info('Final intro narration complete, advancing from lobby to final round');
    try {
      const state = getState();
      const gameObject = state.game?.game;
      if (!gameObject) return;
      const players = Object.values(state.player?.entities || {});
      await advanceState(gameObject, players, dispatch);
    } catch (error) {
      log.error('Error advancing after final intro:', error);
    }
  },
};

const api = {
  trigger: FinalIntroTrigger,
  playback: FinalIntroPlayback,
};

export default api;
